import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { HelperServerApiServiceService } from 'src/app/services/helper-server-api-service.service';
import { AuthGuard } from 'src/app/shared/auth.service'

@Component({
  selector: 'app-edit-user-access',
  templateUrl: './edit-user-access.component.html',
  styleUrls: ['./edit-user-access.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * User-access management page.
 *
 * Guards entry on the admin-only bar (`canAccessAdminTabs`:
 * `userAccessGroup >= 10000000` + `canManageKeycloakUsers()`/
 * `canManageJSreportUsers()` + `portal-admin`). Navigates to `/` if it
 * doesn't hold.
 *
 * **2026-09-13 (owner): the three agent-admin tabs this page used to also
 * host (Команда агента, Банківські облікові дані, Заблоковані контрагенти
 * (банк)) moved to their own page, `/agent-setup`** — this page is back to
 * being purely Keycloak/security-group user access management, its
 * original scope. `canManageBlockedCounterparties` (the finance-manager/
 * can-manage-payments OR that only the blocked-counterparties tab needed)
 * moved with it; see `AgentSetupComponent`.
 */
export class EditUserAccessComponent implements OnInit, OnDestroy {

  public userAccessGroup: number;
  public mainCompany: number;

  /** `true` when the user meets the admin-only bar this page's two
   *  remaining tabs (Пользователи и доступ / Группы доступа) require. */
  public canAccessAdminTabs: boolean = false;

  windowsTitle: string = 'Настройка доступа - Пользователи и Группы';


  private HelperServerQuery: Subscription;

  demoServerApiText: string = 'This is a demo server API. It is not connected to any real database.';
  userInfoLoaded: boolean = false;

  constructor(
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title,
    private helperApi: HelperServerApiServiceService,
    private router: Router,
    private authGuard: AuthGuard
  ) {
  }

  /** Unsubscribes the helper-server query. */
  ngOnDestroy(): void {
    console.log('*** EditUserAccessComponent ngOnDestroy: ')

    if (this.HelperServerQuery) { this.HelperServerQuery.unsubscribe(); }
  }

  /** Guards access, loads Keycloak user info, and sets the browser title. */
  ngOnInit(): void {
    console.log('*** EditUserAccessComponent onInit');

    this.globalsettingsservice.getGlobalSettings();

    this.userAccessGroup = this.globalsettingsservice.getAccessGroup();

    // Check access rights - prevent direct URL access
    // webapi requires realm:portal-admin specifically on every SecurityGroups*/
    // SecurityGroupMember(s) route this page's two tabs call
    // (SEC-SGM-ROLE-1) — mirror that here so a can-manage-users-only user
    // (still allowed by canManageKeycloakUsers/canManageJSreportUsers) doesn't
    // land on a page where every request 403s.
    const requiredAccessGroup = 10000000; // Adjust as needed
    this.canAccessAdminTabs =
      (this.userAccessGroup >= requiredAccessGroup)
      && this.authGuard.canManageKeycloakUsers()
      && this.authGuard.canManageJSreportUsers()
      && this.globalsettingsservice.hasSomeRoles('portal-admin');

    if (!this.canAccessAdminTabs) {
      alert('У вас недостаточно прав для доступа к этой странице.');
      this.router.navigate(['/']);
      return;
    }

    this.mainCompany = this.globalsettingsservice.getMainCompanyId();

    this.titleService.setTitle(this.windowsTitle);

    // getObservable2KeycloakUserInfo()'s only consumer, demoServerApiText, is
    // rendered solely inside the commented-out "Helper Service Api Test"
    // accordion tab in this component's own template -- dead code. But the
    // call itself is not: HelperServerApiServiceService's private
    // _getObservableApiUrl() unconditionally requires BOTH
    // canManageJSreportUsers() AND canManageKeycloakUsers(), throwing (and
    // window.alert()-ing "Insufficient permissions to manage Keycloak
    // users") for anyone lacking either. Safe to call unconditionally here:
    // the early return above already guarantees canAccessAdminTabs (which
    // itself requires both) is true for anyone who reaches this line -- this
    // page no longer admits a lesser-privileged user the way it briefly did
    // while it also hosted the blocked-counterparties tab.
    this.HelperServerQuery =
      this.helperApi.getObservable2KeycloakUserInfo().pipe(
        map(data => {
          this.demoServerApiText = JSON.stringify(data, null, 2);
          this.userInfoLoaded = true;
        })
      ).subscribe();
  }

  /** Logs the opened accordion tab index (diagnostic only). */
  onTabOpen(e) {
    var index = e.index;
    console.log('*** EditUserAccessComponent onTabOpen: ' + index);
  }
}
